/**
 * @file Server-side event services for the calendar library.
 * These services handle all database operations and business logic for events.
 */

import { DataSource, Between, Repository } from 'typeorm';
import { Event, Calendar, EventIncome, EventExpense, Venue, Contact } from '../database/entities';
import type { Changes, FinancialSummary } from '../../shared/types';

let db: DataSource;
let eventRepository: Repository<Event>;
let calendarRepository: Repository<Calendar>;
let incomeRepository: Repository<EventIncome>;
let expenseRepository: Repository<EventExpense>;
let venueRepository: Repository<Venue>;
let contactRepository: Repository<Contact>;

/**
 * Initializes the database connection and repositories.
 */
export function initDb(dataSource: DataSource): DataSource {
  db = dataSource;
  eventRepository = dataSource.getRepository(Event);
  calendarRepository = dataSource.getRepository(Calendar);
  incomeRepository = dataSource.getRepository(EventIncome);
  expenseRepository = dataSource.getRepository(EventExpense);
  venueRepository = dataSource.getRepository(Venue);
  contactRepository = dataSource.getRepository(Contact);
  return db;
}

function getDb(): DataSource {
  if (!db) {
    throw new Error('Database has not been initialized. Call initDb() first.');
  }
  return db;
}

/**
 * Fetches changes to events since a given date for sync purposes.
 */
export async function fetchChanges(calendarId: string, since: Date): Promise<Changes> {
  const eventRepository = getDb().getRepository(Event);

  const updated = await eventRepository.find({
    where: {
      calendar: { id: calendarId },
      updatedAt: Between(since, new Date()),
    },
    relations: ['calendar', 'venue', 'primaryContact'],
  });

  // TypeORM doesn't have a built-in way to track deletions,
  // so we assume the consumer application handles this, possibly with a soft-delete mechanism.
  // For this implementation, we'll return an empty array for deleted events.
  const deleted: string[] = [];

  return {
    updated: updated.map(event => ({
      id: event.id,
      summary: event.summary,
      description: event.description,
      start: event.start,
      end: event.end,
      recurrenceRule: event.recurrenceRule,
      type: event.type,
      genre: event.genre,
      instrument: event.instrument,
      difficulty: event.difficulty,
      repertoire: event.repertoire,
      setList: event.setList,
      equipmentNeeded: event.equipmentNeeded,
      dresscode: event.dresscode,
      soundcheckTime: event.soundcheckTime,
      loadInTime: event.loadInTime,
      paymentStatus: event.paymentStatus,
      paymentDueDate: event.paymentDueDate,
      studentLevel: event.studentLevel,
      lessonFocus: event.lessonFocus,
      auditionPiece: event.auditionPiece,
      auditionRequirements: event.auditionRequirements,
      practiceGoals: event.practiceGoals,
      rehearsalNotes: event.rehearsalNotes,
      status: event.status,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt,
      calendarId: event.calendar.id,
      venueId: event.venue?.id,
      primaryContactId: event.primaryContact?.id,
    })),
    deleted,
  };
}

/**
 * Creates a new event.
 */
export async function createEvent(
  calendarId: string,
  eventData: Omit<Event, 'id' | 'updatedAt' | 'createdAt' | 'calendar'>
): Promise<Event> {
  const eventRepository = getDb().getRepository(Event);
  const calendarRepository = getDb().getRepository(Calendar);
  
  const calendar = await calendarRepository.findOneBy({ id: calendarId });
  if (!calendar) {
    throw new Error(`Calendar with ID "${calendarId}" not found.`);
  }

  const newEvent = eventRepository.create({
    ...eventData,
    calendar,
  });
  return eventRepository.save(newEvent);
}

/**
 * Updates an existing event.
 */
export async function updateEvent(
  eventId: string,
  updates: Partial<Omit<Event, 'id' | 'calendar' | 'createdAt' | 'updatedAt'>>
): Promise<Event> {
  const eventRepository = getDb().getRepository(Event);
  
  await eventRepository.update(eventId, updates);
  const updatedEvent = await eventRepository.findOneBy({ id: eventId });
  
  if (!updatedEvent) {
    throw new Error(`Event with ID "${eventId}" not found.`);
  }
  
  return updatedEvent;
}

/**
 * Deletes an event.
 */
export async function deleteEvent(eventId: string): Promise<void> {
  const eventRepository = getDb().getRepository(Event);
  await eventRepository.delete({ id: eventId });
}

/**
 * Gets all events for a calendar.
 */
export async function getEventsByCalendar(calendarId: string): Promise<Event[]> {
  const eventRepository = getDb().getRepository(Event);
  return eventRepository.find({
    where: { calendar: { id: calendarId } },
    relations: ['calendar', 'venue', 'primaryContact', 'income', 'expenses'],
  });
}

/**
 * Gets events by type for a calendar.
 */
export async function getEventsByType(calendarId: string, type: string): Promise<Event[]> {
  const eventRepository = getDb().getRepository(Event);
  return eventRepository.find({
    where: { 
      calendar: { id: calendarId },
      type: type
    },
    relations: ['calendar', 'venue', 'primaryContact', 'income', 'expenses'],
    order: { start: 'ASC' }
  });
}

/**
 * Gets upcoming gigs for a calendar.
 */
export async function getUpcomingGigs(calendarId: string, limit: number = 10): Promise<Event[]> {
  const eventRepository = getDb().getRepository(Event);
  const now = new Date();
  
  return eventRepository.find({
    where: { 
      calendar: { id: calendarId },
      type: 'gig',
      start: Between(now, new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000)) // Next year
    },
    relations: ['calendar', 'venue', 'primaryContact', 'income', 'expenses'],
    order: { start: 'ASC' },
    take: limit
  });
}

/**
 * Gets student lessons for a specific student contact.
 */
export async function getStudentLessons(calendarId: string, studentContactId: string): Promise<Event[]> {
  const eventRepository = getDb().getRepository(Event);
  
  return eventRepository.find({
    where: { 
      calendar: { id: calendarId },
      type: 'lesson',
      primaryContact: { id: studentContactId }
    },
    relations: ['calendar', 'venue', 'primaryContact', 'income', 'expenses'],
    order: { start: 'ASC' }
  });
}

/**
 * Adds income to an event.
 */
export async function addEventIncome(eventId: string, income: Omit<EventIncome, 'id' | 'event'>): Promise<EventIncome> {
  const event = await eventRepository.findOne({ where: { id: eventId } });
  if (!event) {
    throw new Error(`Event with id ${eventId} not found`);
  }

  const eventIncome = incomeRepository.create({
    ...income,
    event
  });

  return await incomeRepository.save(eventIncome);
}

/**
 * Adds expense to an event.
 */
export async function addEventExpense(eventId: string, expense: Omit<EventExpense, 'id' | 'event'>): Promise<EventExpense> {
  const event = await eventRepository.findOne({ where: { id: eventId } });
  if (!event) {
    throw new Error(`Event with id ${eventId} not found`);
  }

  const eventExpense = expenseRepository.create({
    ...expense,
    event
  });

  return await expenseRepository.save(eventExpense);
}

/**
 * Gets all income for an event.
 */
export async function getEventIncome(eventId: string): Promise<EventIncome[]> {
  return await incomeRepository.find({
    where: { event: { id: eventId } }
  });
}

/**
 * Gets all expenses for an event.
 */
export async function getEventExpenses(eventId: string): Promise<EventExpense[]> {
  return await expenseRepository.find({
    where: { event: { id: eventId } }
  });
}

/**
 * Calculates total income for an event.
 */
export async function calculateEventIncome(eventId: string): Promise<number> {
  const income = await getEventIncome(eventId);
  return income.reduce((total, item) => total + Number(item.amount), 0);
}

/**
 * Calculates total expenses for an event.
 */
export async function calculateEventExpenses(eventId: string): Promise<number> {
  const expenses = await getEventExpenses(eventId);
  return expenses.reduce((total, item) => total + Number(item.amount), 0);
}

/**
 * Calculates net profit/loss for an event.
 */
export async function calculateEventProfit(eventId: string): Promise<number> {
  const totalIncome = await calculateEventIncome(eventId);
  const totalExpenses = await calculateEventExpenses(eventId);
  return totalIncome - totalExpenses;
}

/**
 * Gets financial summary for multiple events.
 */
export async function getFinancialSummary(eventIds: string[]): Promise<FinancialSummary> {
  let totalIncome = 0;
  let totalExpenses = 0;

  for (const eventId of eventIds) {
    totalIncome += await calculateEventIncome(eventId);
    totalExpenses += await calculateEventExpenses(eventId);
  }

  const netProfit = totalIncome - totalExpenses;
  const eventCount = eventIds.length;
  const averageProfitPerEvent = eventCount > 0 ? netProfit / eventCount : 0;

  return {
    totalIncome,
    totalExpenses,
    netProfit,
    eventCount,
    averageProfitPerEvent,
  };
}

/**
 * Updates event income.
 */
export async function updateEventIncome(incomeId: string, updates: Partial<EventIncome>): Promise<EventIncome> {
  await incomeRepository.update(incomeId, updates);
  const updatedIncome = await incomeRepository.findOneBy({ id: incomeId });
  
  if (!updatedIncome) {
    throw new Error(`Income with ID "${incomeId}" not found.`);
  }
  
  return updatedIncome;
}

/**
 * Updates event expense.
 */
export async function updateEventExpense(expenseId: string, updates: Partial<EventExpense>): Promise<EventExpense> {
  await expenseRepository.update(expenseId, updates);
  const updatedExpense = await expenseRepository.findOneBy({ id: expenseId });
  
  if (!updatedExpense) {
    throw new Error(`Expense with ID "${expenseId}" not found.`);
  }
  
  return updatedExpense;
}

/**
 * Deletes event income.
 */
export async function deleteEventIncome(incomeId: string): Promise<void> {
  await incomeRepository.delete({ id: incomeId });
}

/**
 * Deletes event expense.
 */
export async function deleteEventExpense(expenseId: string): Promise<void> {
  await expenseRepository.delete({ id: expenseId });
}

/**
 * Creates a new venue.
 */
export async function createVenue(venue: Omit<Venue, 'id'>): Promise<Venue> {
  const newVenue = venueRepository.create(venue);
  return await venueRepository.save(newVenue);
}

/**
 * Creates a new contact.
 */
export async function createContact(contact: Omit<Contact, 'id'>): Promise<Contact> {
  const newContact = contactRepository.create(contact);
  return await contactRepository.save(newContact);
} 